export type IntervalUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export const UNIT_SECONDS: Record<IntervalUnit, number> = { seconds: 1, minutes: 60, hours: 3600, days: 86400 };

/** An interval as the largest unit it is a whole number of, for the edit form. */
export function splitInterval(seconds: number): { value: number; unit: IntervalUnit } {
  for (const unit of ['days', 'hours', 'minutes'] as IntervalUnit[]) {
    const size = UNIT_SECONDS[unit];
    if (seconds >= size && seconds % size === 0) return { value: seconds / size, unit };
  }
  return { value: seconds, unit: 'seconds' };
}

export function toSeconds(value: number, unit: IntervalUnit): number {
  return Math.round(value * UNIT_SECONDS[unit]);
}

/** "every 30 minutes", "every hour". */
export function describeEvery(seconds: number): string {
  const { value, unit } = splitInterval(seconds);
  const word = unit.slice(0, -1);
  return value === 1 ? `every ${word}` : `every ${value} ${unit}`;
}

/** "in 4 min", "2 h ago", "now". */
export function relativeTime(iso: string, now: number): string {
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  if (abs < 5000) return 'now';
  const text = abs < 60_000 ? `${Math.round(abs / 1000)} s`
    : abs < 3_600_000 ? `${Math.round(abs / 60_000)} min`
      : abs < 86_400_000 ? `${Math.round(abs / 3_600_000)} h`
        : `${Math.round(abs / 86_400_000)} d`;
  return diff > 0 ? `in ${text}` : `${text} ago`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

/** Tasks by their group, groups in the order they first appear. */
export function groupTasks<T extends { group: string }>(tasks: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const t of tasks) groups.set(t.group, [...(groups.get(t.group) ?? []), t]);
  return [...groups.entries()];
}
